import { Link } from "react-router-dom";
import Logo from "./Logo";
import CardBrands from "@/pages/checkout/components/CardBrands";
import { openCookieSettings } from "@/lib/cookieConsent";
import { isPayHost, payAppUrl } from "@/lib/payHost";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** A main-site path — absolute on the pay host, which serves only /pay/:id. */
function siteUrl(path: string): string {
  return isPayHost() ? `${payAppUrl()}${path}` : path;
}

function SiteLink({ to, className, children }: { to: string; className: string; children: ReactNode }) {
  if (isPayHost()) {
    return (
      <a href={siteUrl(to)} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="relative bg-primary-950 text-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="sm:col-span-2">
            <Logo dark />
            <p className="mt-5 text-sm leading-relaxed text-background-50/70 max-w-sm">
              {t("home.footer.about")}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-accent-200">{t("home.footer.company")}</h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a href={siteUrl("/#how-it-works")} className="text-background-50/70 hover:text-background-50 transition-colors">
                  {t("home.footer.howItWorks")}
                </a>
              </li>
              <li>
                <a href={siteUrl("/#demo")} className="text-background-50/70 hover:text-background-50 transition-colors">
                  {t("common.nav.demo")}
                </a>
              </li>
              <li>
                <a href={siteUrl("/#pricing")} className="text-background-50/70 hover:text-background-50 transition-colors">
                  {t("common.nav.pricing")}
                </a>
              </li>
              <li>
                <a href={siteUrl("/#faq")} className="text-background-50/70 hover:text-background-50 transition-colors">
                  {t("common.nav.faq")}
                </a>
              </li>
              <li>
                <a href={siteUrl("/#contact")} className="text-background-50/70 hover:text-background-50 transition-colors">
                  {t("common.nav.contact")}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-accent-200">{t("home.footer.legal")}</h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <SiteLink to="/privacy-policy" className="text-background-50/70 hover:text-background-50 transition-colors">
                  {t("common.legal.privacyPolicy")}
                </SiteLink>
              </li>
              <li>
                <SiteLink to="/terms-of-use" className="text-background-50/70 hover:text-background-50 transition-colors">
                  {t("common.legal.termsOfUse")}
                </SiteLink>
              </li>
              <li>
                <SiteLink to="/refund-policy" className="text-background-50/70 hover:text-background-50 transition-colors">
                  {t("common.legal.refundPolicy")}
                </SiteLink>
              </li>
              <li>
                <SiteLink to="/delivery-policy" className="text-background-50/70 hover:text-background-50 transition-colors">
                  {t("common.legal.deliveryPolicy")}
                </SiteLink>
              </li>
              <li>
                <SiteLink to="/cookie-policy" className="text-background-50/70 hover:text-background-50 transition-colors">
                  {t("common.legal.cookiePolicy")}
                </SiteLink>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openCookieSettings}
                  className="text-background-50/70 hover:text-background-50 transition-colors cursor-pointer"
                >
                  {t("common.cookies.footerLink")}
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-accent-200">{t("home.footer.contact")}</h4>
            <ul className="mt-4 space-y-3 text-sm text-background-50/70">
              <li className="flex items-start gap-2">
                <i className="ri-map-pin-2-line text-accent-200 mt-0.5"></i>
                <span>{t("home.contact.address")}</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-phone-line text-accent-200"></i>
                <a href="tel:+359886886479" className="hover:text-background-50 transition-colors">
                  +359 886 886 479
                </a>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-accent-200"></i>
                <a href="mailto:Info@aistylemedia.com" className="hover:text-background-50 transition-colors">
                  Info@aistylemedia.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-customer-service-2-line text-accent-200"></i>
                <a href="mailto:support@aistylemedia.com" className="hover:text-background-50 transition-colors">
                  support@aistylemedia.com
                </a>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-time-line text-accent-200 mt-0.5"></i>
                <span>{t("home.contact.hours")}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-background-50/10 flex flex-col md:flex-row md:items-center gap-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-accent-200">
            {t("home.footer.securePayments")}
          </span>
          <CardBrands secure onDark />
        </div>

        <div className="mt-6 pt-6 border-t border-background-50/10 text-xs text-background-50/60">
          <p>
            {t("home.footer.registered")}
          </p>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-background-50/50">
          <span>{t("home.footer.copyright", { year: new Date().getFullYear() })}</span>
          <span className="flex items-center gap-2">
            <i className="ri-shield-check-line text-accent-200"></i>
            {t("home.footer.builtIn")}
          </span>
        </div>
      </div>
    </footer>
  );
}